import { useCallback, useState } from "react";
import { useMutation } from "urql";
import { useTranslate } from "hooks/useTranslate";
import { SUPPORT_INQUIRY_MUTATION } from "api/mutations/support";

export const useConfiguratorSupport = () => {
  const translate = useTranslate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [{ fetching, error: mutationError }, execute] = useMutation(
    SUPPORT_INQUIRY_MUTATION,
  );

  const handleSendMail = useCallback(
    async (formData) => {
      if (!formData || !formData.message) {
        setError(translate("MESSAGE_REQUIRED"));
        return false;
      }

      setIsSubmitting(true);
      setError(null);
      setSuccess(false);

      try {
        const attachments = formData.attachments
          ? formData.attachments.filter(
              (file) => file !== null && file !== undefined,
            )
          : [];

        const input = {
          from: formData.email && formData.email.trim() ? formData.email : null,
          subject:
            formData.subject && formData.subject.trim()
              ? formData.subject
              : null,
          type: formData.type || "BUG",
          message: formData.message.trim(),
          attachments:
            attachments.length > 0 ? attachments.map(() => null) : null,
        };

        if (attachments.length > 0) {
          const mutation = `mutation SupportInquiry($input: SupportInquiry!) { supportInquiry(input: $input) }`;

          const operations = JSON.stringify({
            query: mutation,
            variables: { input },
          });

          const map = {};
          attachments.forEach((_, idx) => {
            map[idx] = [`variables.input.attachments.${idx}`];
          });

          const form = new FormData();
          form.append("operations", operations);
          form.append("map", JSON.stringify(map));
          attachments.forEach((file, idx) => {
            form.append(String(idx), file, file.name || `file-${idx}`);
          });

          const url = `${process.env.ASSISTIVE_BACKEND_URL}/graphql`;
          const resp = await fetch(url, {
            method: "POST",
            body: form,
            credentials: "include",
          });

          const json = await resp.json().catch(() => null);
          if (!resp.ok) {
            const errMsg =
              json?.errors?.[0]?.message || translate("SUBMISSION_FAILED");
            setError(errMsg);
            return false;
          }
          if (json?.errors?.length) {
            setError(
              json.errors.map((e) => e.message).join("; ") ||
                translate("SUBMISSION_FAILED"),
            );
            return false;
          }
          if (json?.data?.supportInquiry) {
            setSuccess(true);
            return true;
          }
          setError(translate("SUBMISSION_FAILED"));
          return false;
        }

        const result = await execute({ input });

        if (result.error) {
          setError(result.error.message || translate("SUBMISSION_FAILED"));
          return false;
        }

        if (result.data?.supportInquiry) {
          setSuccess(true);
          return true;
        }

        setError(translate("SUBMISSION_FAILED"));
        return false;
      } catch (err) {
        const errorMsg = err?.message || translate("UNEXPECTED_ERROR");
        setError(errorMsg);
        console.error("Support inquiry error:", err);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [execute, translate],
  );

  const resetState = useCallback(() => {
    setError(null);
    setSuccess(false);
    setIsSubmitting(false);
  }, []);

  return {
    handleSendMail,
    isSubmitting: isSubmitting || fetching,
    error: error || mutationError?.message,
    success,
    resetState,
  };
};
